/**
 * Shared rules for the manifest email-intake allowlist.
 *
 * Lives here rather than in the receive-email lambda because three callers need the
 * same answers: the lambda (which address a message resolves against), the API
 * (rejecting a bad entry on write), and Oak (showing the reason inline before the
 * user saves). A second copy of any of these rules would drift.
 */

export type EmailIntakeSenderMatchType = "ADDRESS" | "DOMAIN";

/**
 * The inbound intake address for an environment.
 *
 * `release` receives via a Google Workspace redirect, so the address customers see
 * is on the bare encamp.com domain; internal environments are addressed directly on
 * their own subdomain. Keeping this branch in one place matters — a duplicate in the
 * frontend would show the wrong address on exactly the environment that counts.
 *
 * `env` is required rather than optional on purpose. An absent env interpolates to
 * "manifests@undefined.env.encamp.com", which looks real enough that staff would
 * hand it to a customer, so callers have to decide what to do about a missing one
 * instead of being handed a plausible lie. The compiler enforces that; this stays a
 * total function so it is safe to call at module scope.
 */
export function intakeEmailAddress(env: string): string {
  const trimmed = env.trim();
  return `manifests@${trimmed === "release" ? "encamp.com" : `${trimmed}.env.encamp.com`}`;
}

/**
 * Every strict parent of a domain, most specific first: "a.b.example.com" yields
 * ["b.example.com", "example.com", "com"].
 *
 * Shared with sender resolution so the allowlist's overlap warnings and the runtime
 * tiering agree on what "a subdomain of" means. Matching a generated parent for
 * equality is what makes it safe — a suffix test would treat evil-example.com as a
 * subdomain of example.com.
 */
export function parentDomainsOf(domain: string): string[] {
  const labels = domain.split(".");
  const parents: string[] = [];
  for (let i = 1; i < labels.length; i++) {
    parents.push(labels.slice(i).join("."));
  }
  return parents;
}

/**
 * Consumer mail providers, which must never be allowlisted at domain granularity —
 * that would trust every account on the provider. Address-level entries for these
 * are fine and genuinely needed: a one-person hauler on Gmail is a real customer.
 */
export const PUBLIC_EMAIL_PROVIDER_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "ymail.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "gmx.com",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "fastmail.com",
] as const;

const ENCAMP_DOMAIN = "encamp.com";

/**
 * Trim, lowercase, drop a display-name wrapper and a leading "@" from domains.
 *
 * Callers should normalize before validating and before storing, so that what was
 * checked is what lands in the column.
 */
export function normalizeSenderValue(matchType: EmailIntakeSenderMatchType, value: string): string {
  let normalized = value.trim().toLowerCase();

  // "Display Name <a@b.com>" — keep only the address.
  const angled = normalized.match(/<([^>]+)>\s*$/);
  if (angled) {
    normalized = angled[1].trim();
  }

  if (matchType === "DOMAIN") {
    normalized = normalized.replace(/^@+/, "");
  }

  return normalized;
}

/** A human-readable reason the value can't be saved, or null when it is acceptable. */
export function validateSenderValue(
  matchType: EmailIntakeSenderMatchType,
  value: string
): string | null {
  const normalized = normalizeSenderValue(matchType, value);

  if (!normalized) {
    return "Enter an email address or domain.";
  }

  if (matchType === "ADDRESS") {
    const parts = normalized.split("@");
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return "Enter a full email address, like dispatch.dallas@vendor.com.";
    }
    if (!parts[1].includes(".")) {
      return "That email address's domain looks incomplete.";
    }
    return null;
  }

  if (normalized.includes("@")) {
    return "Enter a domain only, with no @ — for example vendor.com.";
  }
  if (!normalized.includes(".")) {
    return "Enter a full domain, like vendor.com.";
  }
  if (normalized.startsWith(".") || normalized.endsWith(".") || normalized.includes("..")) {
    return "That domain isn't valid.";
  }
  if (PUBLIC_EMAIL_PROVIDER_DOMAINS.some((provider) => provider === normalized)) {
    return `${normalized} is a public email provider, so allowlisting the whole domain would trust every account on it. Add the specific email address instead.`;
  }
  return null;
}

/**
 * A reason to think twice, which does not stop the save.
 *
 * Separate from `validateSenderValue` because the two answer different questions.
 * Allowlisting a public provider is never right, so that stays a refusal. Allowlisting
 * `encamp.com` as a domain is *usually* wrong — every Encamp employee's forwarded mail
 * would land in one customer's documents, and staff forwarding is ENG-16354's problem
 * rather than something a blanket entry should paper over — but it is legitimately
 * right for Encamp's own test tenant, which is how this feature is exercised
 * internally and which the TenantDomain backfill carries across.
 *
 * Refusing it outright made that migrated row impossible to re-save through the
 * dialog, and there is no reliable way to recognise our own tenant: the id differs per
 * environment and no constant names it. So this warns and lets a deliberate choice
 * through, the same way a cross-tenant collision does.
 */
export function adviseOnSenderValue(matchType: "ADDRESS" | "DOMAIN", value: string): string | null {
  if (matchType !== "DOMAIN") return null;
  const normalized = normalizeSenderValue("DOMAIN", value);
  if (normalized !== ENCAMP_DOMAIN) return null;
  return "Every Encamp employee's mail would route to this organization. That is almost always wrong for a customer — add the specific address instead. Correct only for Encamp's own test organization.";
}
